import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Loader2, FolderKanban, AlertCircle } from "lucide-react";
import { PageHeader } from "@/components/shared/PageHeader";
import {
  useDeliveryProjects,
  deliveryStatus,
  progressLabel,
  progressCaption,
  type DeliveryStatusTone,
} from "@/hooks/useDeliveryProjects";
import { DeliveryIssues } from "@/components/admin/DeliveryIssues";
import { useLinearIssues } from "@/hooks/useLinear";
import { format, isPast } from "date-fns";
import { ptBR } from "date-fns/locale";

const SEG_TRIGGER =
  "rounded-lg px-4 py-2 text-[12.5px] font-semibold text-muted-foreground data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm";

// Top-level "/admin/projetos" screen (Task 7a — Entrega module). Owns the
// 2-tab shell (Visão geral / Issues) per the redesign mockup's "projetos"
// screen; the grid below is this tab's content, and the Issues tab renders
// <DeliveryIssues/> — Linear's new home in the admin IA now that Task 3
// dropped the standalone sidebar entry (still reachable directly via
// /admin/linear/* too, unchanged).

const TONE_CLASSES: Record<DeliveryStatusTone, string> = {
  neutral: "bg-muted text-muted-foreground",
  warn: "bg-warn-soft text-warn",
  ok: "bg-ok-soft text-ok",
};

type ProjTab = "overview" | "issues";
function resolveTab(param: string | null): ProjTab {
  return param === "issues" ? "issues" : "overview";
}

export function DeliveryOverview() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = resolveTab(searchParams.get("tab"));
  const { data: projects = [], isLoading, error } = useDeliveryProjects();
  const { data: linearIssues } = useLinearIssues(null);
  const issueCount = linearIssues?.issues?.nodes ? linearIssues.issues.nodes.length : null;

  const overdueCount = useMemo(
    () =>
      projects.filter((p) => {
        if (!p.due_date) return false;
        if (p.progress === 100) return false;
        return isPast(new Date(p.due_date));
      }).length,
    [projects],
  );

  const handleTabChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === "overview") next.delete("tab");
    else next.set("tab", value);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="mx-auto flex max-w-[1200px] flex-col gap-[22px]">
      <PageHeader
        title="Projetos"
        description={
          <>
            Sincronizado com o Linear
            {!isLoading && projects.length > 0 && (
              <>
                {" "}
                · {projects.length} projeto{projects.length !== 1 ? "s" : ""}
                {overdueCount > 0 && `, ${overdueCount} atrasado${overdueCount !== 1 ? "s" : ""}`}
              </>
            )}
          </>
        }
      />

      <Tabs value={activeTab} onValueChange={handleTabChange} className="flex flex-col gap-[22px]">
        <TabsList className="h-auto w-fit flex-wrap gap-[3px] rounded-[10px] bg-muted p-[3px]">
          <TabsTrigger value="overview" className={SEG_TRIGGER}>
            Visão geral
          </TabsTrigger>
          <TabsTrigger value="issues" className={SEG_TRIGGER}>
            Issues{issueCount !== null && ` · ${issueCount}`}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-0">
          {isLoading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {!isLoading && error && (
            <div className="flex items-start gap-3 rounded-2xl border border-bad/25 bg-card p-6 shadow-card">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-bad" />
              <div>
                <p className="text-sm font-medium">Não foi possível carregar os projetos.</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {error instanceof Error ? error.message : "Tente novamente em instantes."}
                </p>
              </div>
            </div>
          )}

          {!isLoading && !error && projects.length === 0 && (
            <div className="space-y-3 rounded-2xl border border-border bg-card p-10 text-center shadow-card">
              <FolderKanban className="mx-auto h-8 w-8 text-muted-foreground opacity-40" />
              <p className="text-sm text-muted-foreground">
                Nenhum projeto de cliente cadastrado ainda.
              </p>
              <p className="text-xs text-muted-foreground">
                Projetos são criados a partir de um lead, na etapa "Projeto" do fluxo de vendas.
              </p>
            </div>
          )}

          {!isLoading && !error && projects.length > 0 && (
            <div className="grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-4">
              {projects.map((p) => {
                const status = deliveryStatus(p);
                const barTone =
                  status.tone === "ok"
                    ? "bg-ok"
                    : status.tone === "warn"
                      ? "bg-warn"
                      : "bg-primary";
                return (
                  <Link
                    key={p.id}
                    to={`/admin/projetos/${p.id}`}
                    className="block rounded-2xl border border-border bg-card px-[22px] py-5 shadow-card transition-colors hover:bg-muted/30"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="text-[14.5px] font-bold leading-[1.3] tracking-[-0.01em]">
                        {p.title}
                      </h3>
                      <Badge
                        className={`h-[22px] shrink-0 whitespace-nowrap rounded-[7px] border-transparent px-[9px] text-[10.5px] font-bold ${TONE_CLASSES[status.tone]}`}
                      >
                        {status.label}
                      </Badge>
                    </div>
                    {p.description && (
                      <p className="mt-[7px] line-clamp-2 text-[12.5px] leading-[1.5] text-muted-foreground">
                        {p.description}
                      </p>
                    )}

                    <div className="mt-[18px] flex items-center justify-between text-[11.5px] text-muted-foreground">
                      <span>Progresso</span>
                      <span className="font-bold" title={progressCaption(p) ?? undefined}>
                        {progressLabel(p)}
                      </span>
                    </div>
                    <div className="mt-[7px] h-1.5 overflow-hidden rounded-full bg-muted">
                      <div
                        className={`h-full rounded-full ${barTone}`}
                        style={{ width: p.progress === null ? "0%" : `${p.progress}%` }}
                      />
                    </div>
                    {p.progressBasis === "phase" && p.progress !== null && (
                      <p className="mt-1 text-[10px] text-muted-foreground">{progressCaption(p)}</p>
                    )}

                    <div className="mt-3.5 flex items-center gap-3.5 text-[11.5px] text-muted-foreground">
                      <span>
                        {p.due_date
                          ? format(new Date(p.due_date), "dd/MM/yyyy", { locale: ptBR })
                          : "Sem prazo"}
                      </span>
                      <span>·</span>
                      <span className="truncate">{p.client_name ?? "Sem cliente vinculado"}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="issues" className="mt-0">
          <DeliveryIssues />
        </TabsContent>
      </Tabs>
    </div>
  );
}
